import { API_URL } from '../config/apiConfig';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('No authentication token found');
    }
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

export const getCurrentWeekTimecards = async (queryParams = '') => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/current-week${queryParams}`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            // Return empty array for 404 (no timecards found)
            if (response.status === 404) {
                return [];
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch timecards');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getCurrentWeekTimecards:', error);
        // Handle 503 Service Unavailable specifically
        if (error.status === 503) {
            throw new Error('Service temporarily unavailable. Please try again later.');
        }
        // Return empty array for 404 or if error includes "not found"
        if (error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        throw error;
    }
};

export const getWeekTimecards = async (date, userId = null, viewType = null) => {
    try {
        let url = `${API_URL}/api/timecards/week/${date}`;
        const params = new URLSearchParams();
        
        if (userId) {
            params.append('userId', userId);
        }
        if (viewType) {
            params.append('viewType', viewType);
        }
        
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
            
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return [];
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch timecards');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getWeekTimecards:', error);
        if (error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        throw error;
    }
};

export const getMonthTimecards = async (date, userId = null, viewType = null) => {
    try {
        let url = `${API_URL}/api/timecards/month/${date}`;
        const params = new URLSearchParams();
        
        if (userId) {
            params.append('userId', userId);
        }
        if (viewType) {
            params.append('viewType', viewType);
        }
        
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
            
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return [];
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch timecards');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getMonthTimecards:', error);
        if (error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        throw error;
    }
};

export const getYearTimecards = async (year, userId = null, viewType = null) => {
    try {
        let url = `${API_URL}/api/timecards/year/${year}`;
        const params = new URLSearchParams();
        
        if (userId) {
            params.append('userId', userId);
        }
        if (viewType) {
            params.append('viewType', viewType);
        }
        
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
            
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return [];
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch timecards');
        }
        
        const data = await response.json();
        
        // Add null checks and data validation for timecards
        if (Array.isArray(data)) {
            return data.map(timecard => {
                if (!timecard.user || typeof timecard.user !== 'object') {
                    timecard.user = {
                        name: 'Unknown User',
                        email: 'unknown@email.com'
                    };
                }
                return timecard;
            });
        }
        
        return data;
    } catch (error) {
        console.error('Error in getYearTimecards:', error);
        if (error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        throw error;
    }
};

export const getTestWeekTimecards = async (date) => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/test-week/${date}`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return { weekRange: null, timecards: [] };
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch test week timecards');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getTestWeekTimecards:', error);
        if (error.message.includes('404') || error.message.includes('not found')) {
            return { weekRange: null, timecards: [] };
        }
        throw error;
    }
};

export const getUncertifiedTimecards = async () => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/uncertified`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return [];
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch uncertified timecards');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getUncertifiedTimecards:', error);
        // Handle 503 Service Unavailable specifically
        if (error.status === 503) {
            throw new Error('Service temporarily unavailable. Please try again later.');
        }
        if (error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        throw error;
    }
};

export const getPendingApprovals = async () => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/pending-approval`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return [];
            }
            if (response.status === 503) {
                throw new Error('Offline');
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch pending approvals');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getPendingApprovals:', error);
        if (error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        // Handle network errors and 503 specifically
        if (!navigator.onLine || error.message.includes('Failed to fetch') || error.name === 'TypeError') {
            throw new Error('Offline');
        }
        throw error;
    }
};

export const submitTimecard = async (id) => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/${id}/submit`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to submit timecard');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in submitTimecard:', error);
        throw error;
    }
};

export const approveTimecard = async (id, days) => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/${id}/approve`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ days })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to approve timecard');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in approveTimecard:', error);
        throw error;
    }
};

export const createTimecard = async (timecardData) => {
    try {
        const response = await fetch(`${API_URL}/api/timecards`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                project: timecardData.projectId,
                startDate: timecardData.startDate,
                endDate: timecardData.endDate,
                days: timecardData.days
            }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to create timecard');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in createTimecard:', error);
        throw error;
    }
};

export const updateTimecard = async (id, timecardData, isAdmin = false) => {
    try {
        const endpoint = isAdmin ? `${API_URL}/api/timecards/${id}/admin` : `${API_URL}/api/timecards/${id}`;
        const response = await fetch(endpoint, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                days: timecardData.days
            }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update timecard');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in updateTimecard:', error);
        throw error;
    }
};

export const findTimecards = async (userId, projectId, year, weekNumber) => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/find`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                userId,
                projectId,
                year,
                weekNumber
            }),
        });
        
        if (!response.ok) {
            if (response.status === 404) {
                return [];
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to find timecards');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error in findTimecards:', error);
        if (error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        throw error;
    }
};

export const deleteTimecard = async (id, isAdmin = false) => {
    try {
        const endpoint = isAdmin ? `${API_URL}/api/timecards/${id}/admin` : `${API_URL}/api/timecards/${id}`;
        const response = await fetch(endpoint, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to delete timecard');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in deleteTimecard:', error);
        throw error;
    }
};

export const getWeeksWithTimecards = async (userId, projectId, year) => {
    try {
        const response = await fetch(
            `${API_URL}/api/timecards/weeks-with-timecards?userId=${userId}&projectId=${projectId}&year=${year}`,
            {
                method: 'GET',
                headers: getAuthHeaders(),
            }
        );

        if (!response.ok) {
            if (response.status === 404) {
                return { weekNumbers: [] };
            }
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch weeks with timecards');
        }

        return response.json();
    } catch (error) {
        console.error('Error in getWeeksWithTimecards:', error);
        if (error.message.includes('404') || error.message.includes('not found')) {
            return { weekNumbers: [] };
        }
        throw error;
    }
};

export const createAdminTimecard = async (userId, projectId, year, weekNumber) => {
    try {
        const response = await fetch(`${API_URL}/api/timecards/admin/create`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                userId,
                projectId,
                year,
                weekNumber
            }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to create timecard');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in createAdminTimecard:', error);
        throw error;
    }
};
